import { Component, OnInit, ViewChild } from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { WorkflowDataService } from '../../services/workflow-data.service';
import { Table } from 'primeng/table';

interface KikF1Data {
  id: string;
  orderId: string;
  customerName: string;
  year: number;
  month: string;
  week: number;
  value: string | number;
  quantity: string | number;
  [key: string]: any;
}

@Component({
  selector: 'app-lost-order',
  templateUrl: './lost-order.component.html',
  styleUrls: ['./lost-order.component.scss'],
  providers: [ConfirmationService]
})
export class LostOrderComponent implements OnInit {
  @ViewChild('dt') dt!: Table;

  kikF1Data: KikF1Data[] = [];
  filteredKikF1Data: KikF1Data[] = [];

  // Loading states
  loading = false;
  tableLoading = false;

  // Header and layout states
  isHeaderCollapsed = false;

  // Filter states
  searchText = '';
  selectedMonth: string | null = null;
  selectedCustomer: string | null = null;

  // Dialog states
  showDetailsDialog = false;
  showEditDialog = false;
  showDeleteDialog = false;

  // Selected items and dialog data
  selectedKikF1Item: KikF1Data | null = null;
  editingKikF1Item: Partial<KikF1Data> = {};

  // Available years and months for filters
  availableYears: number[] = [];
  yearOptions: { label: string; value: number }[] = []; // New property for dropdown options
  selectedYear: number | null = null; // Initialize to n
  availableMonths: string[] = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  // Add this with your other properties
weekOptions: { label: string; value: number }[] = [
  { label: 'Week 1', value: 1 },
  { label: 'Week 2', value: 2 },
  { label: 'Week 3', value: 3 },
  { label: 'Week 4', value: 4 },
  { label: 'Week 5', value: 5 },
  { label: 'Total', value: 0 }  // 0 for Total
];

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private workflowDataService: WorkflowDataService
  ) { }

  ngOnInit(): void {
    this.initializeFilters();
    this.loadAllKikF1Data();
  }

  // Header collapse handler
  onHeaderCollapse(collapsed: boolean): void {
    this.isHeaderCollapsed = collapsed;
  }

  // Initialize filter options from loaded data
  private initializeFilters(): void {
    // Initialize with current and recent years as fallback
    const currentYear = new Date().getFullYear();
    this.availableYears = Array.from({ length: 10 }, (_, i) => currentYear - i);
    this.yearOptions = this.availableYears.map(year => ({
      label: year.toString(),
      value: year
    }));
    this.selectedYear = null; // Or currentYear for auto-selection
  }


  // Load all KIK#F1 data - FIXED API RESPONSE HANDLING
  loadAllKikF1Data(): void {
    this.loading = true;
    this.tableLoading = true;

    console.log('Fetching ALL KIK#F1 data');

    this.workflowDataService.getAllKikF1Data().subscribe({
      next: (response: any) => {
        console.log('All KIK#F1 data response:', response);

        // Handle the actual API response structure
        let dataArray: any[] = [];

        if (response && typeof response === 'object') {
          // API returns {totalRecords: 15, kikF1Detail: Array(15)}
          if (response.kikF1Detail && Array.isArray(response.kikF1Detail)) {
            dataArray = response.kikF1Detail;
          }
          // Fallback: check if response.data exists
          else if (response.data && Array.isArray(response.data)) {
            dataArray = response.data;
          }
          // Fallback: if response is already an array
          else if (Array.isArray(response)) {
            dataArray = response;
          }
          // Final fallback: empty array
          else {
            dataArray = [];
          }
        }

        console.log('Parsed KIK#F1 data array:', dataArray);

        // NEW: Sort by newest first (year, month, week descending)
        const monthOrder = {
          'January': 1, 'February': 2, 'March': 3, 'April': 4, 'May': 5, 'June': 6,
          'July': 7, 'August': 8, 'September': 9, 'October': 10, 'November': 11, 'December': 12
        } as const; // 'as const' makes it a readonly tuple for better type inference

        const monthKeys = Object.keys(monthOrder) as (keyof typeof monthOrder)[]; // Extract valid keys for type safety

        dataArray.sort((a: any, b: any) => {
          // Compare years descending (newer first)
          if (b.year !== a.year) {
            return b.year - a.year;
          }
          // Same year: compare months descending
          // Cast month as keyof typeof monthOrder to resolve indexing error
          const monthA = (a.month as keyof typeof monthOrder) ? monthOrder[a.month as keyof typeof monthOrder]! : 0;
          const monthB = (b.month as keyof typeof monthOrder) ? monthOrder[b.month as keyof typeof monthOrder]! : 0;
          if (monthB !== monthA) {
            return monthB - monthA;
          }
          // Same year/month: compare weeks descending
          return (b.week || 0) - (a.week || 0);
        });

        console.log('Sorted KIK#F1 data array (newest first):', dataArray);

        // Filter and validate data using type guard
        this.kikF1Data = dataArray.filter(this.isValidKikF1Data.bind(this));

        console.log('Validated KIK#F1 data:', this.kikF1Data.length, 'records');

        // Initialize filtered data
        this.filteredKikF1Data = [...this.kikF1Data];

        // Update filter options AFTER data is properly set
        this.updateFilterOptions();

        // Apply any existing filters
        this.applyFilters();

        this.loading = false;
        this.tableLoading = false;

        this.messageService.add({
          severity: 'success',
          summary: 'Data Loaded',
          detail: `Loaded ${this.kikF1Data.length} valid KIK#F1 records`,
          life: 3000
        });
      },
      error: (error) => {
        console.error('Error loading KIK#F1 data:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Load Error',
          detail: 'Failed to load KIK#F1 data from backend',
          life: 5000
        });
        this.loading = false;
        this.tableLoading = false;

        // Set empty arrays on error to prevent further issues
        this.kikF1Data = [];
        this.filteredKikF1Data = [];
      }
    });
  }


  // Type guard for KikF1Data validation
  private isValidKikF1Data(item: any): item is KikF1Data {
    if (!item || typeof item !== 'object') {
      return false;
    }

    return (
      typeof item.id === 'string' && item.id.trim() !== '' &&
      typeof item.orderId === 'string' && item.orderId.trim() !== '' &&
      typeof item.customerName === 'string' && item.customerName.trim() !== '' &&
      typeof item.year === 'number' && !isNaN(item.year) && item.year > 0 &&
      typeof item.month === 'string' && item.month.trim() !== '' &&
      typeof item.week === 'number' && !isNaN(item.week) && item.week >= 0 && item.week <= 52 &&
      (typeof item.value === 'string' || typeof item.value === 'number') &&
      (typeof item.quantity === 'string' || typeof item.quantity === 'number')
    );
  }

  private updateFilterOptions(): void {
    if (!this.kikF1Data || this.kikF1Data.length === 0) {
      console.warn('No data available to update filter options');
      this.availableYears = [];
      this.yearOptions = [];
      return;
    }

    // Extract unique years from data
    const uniqueYears = [...new Set(this.kikF1Data.map(item => item.year))]
      .filter(year => typeof year === 'number' && year > 0)
      .sort((a, b) => b - a);

    // Use data years if available, otherwise keep initialized years
    this.availableYears = uniqueYears.length > 0 ? uniqueYears : this.availableYears;

    // Transform to object array for PrimeNG dropdown
    this.yearOptions = this.availableYears.map(year => ({
      label: year.toString(), // Display as string
      value: year // Bind numeric value
    }));

    // Optional: Set default selection to current/first year if not set
    if (this.selectedYear === null && this.yearOptions.length > 0) {
      this.selectedYear = this.yearOptions[0].value;
      this.onYearChange(); // Trigger initial filter
    }

    console.log('Updated available years:', this.availableYears);
    console.log('Year options for dropdown:', this.yearOptions);

    // Extract unique customers (keep as-is, assuming string array works for another dropdown)
    const uniqueCustomers = [...new Set(this.kikF1Data.map(item => item.customerName))]
      .filter(name => name && typeof name === 'string')
      .sort();

    console.log('Unique customers:', uniqueCustomers);
  }

  // Global search filter - FIXED
  onGlobalFilter(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchText = target.value || '';
    this.applyFilters();
  }

  // Apply all active filters - FIXED
  applyFilters(): void {
    // Defensive check: ensure kikF1Data is an array
    if (!this.kikF1Data || !Array.isArray(this.kikF1Data)) {
      console.warn('kikF1Data is not a valid array, resetting to empty');
      this.kikF1Data = [];
      this.filteredKikF1Data = [];
      return;
    }

    let filtered: KikF1Data[] = [...this.kikF1Data];

    // Global search filter
    if (this.searchText && this.searchText.trim()) {
      const searchTerm = this.searchText.toLowerCase().trim();
      filtered = filtered.filter(item => {
        if (!this.isValidKikF1Data(item)) return false;

        return (
          (item.customerName && item.customerName.toLowerCase().includes(searchTerm)) ||
          (item.orderId && item.orderId.toLowerCase().includes(searchTerm)) ||
          (item.month && item.month.toLowerCase().includes(searchTerm)) ||
          (item.id && item.id.toLowerCase().includes(searchTerm))
        );
      });
    }

    // Year filter
    if (this.selectedYear !== null && typeof this.selectedYear === 'number') {
      filtered = filtered.filter(item =>
        this.isValidKikF1Data(item) && item.year === this.selectedYear
      );
    }

    // Month filter
    if (this.selectedMonth && typeof this.selectedMonth === 'string') {
      filtered = filtered.filter(item =>
        this.isValidKikF1Data(item) && item.month === this.selectedMonth
      );
    }

    // Customer filter
    if (this.selectedCustomer && typeof this.selectedCustomer === 'string') {
      filtered = filtered.filter(item =>
        this.isValidKikF1Data(item) && item.customerName === this.selectedCustomer
      );
    }

    this.filteredKikF1Data = filtered;

    // Update message only if filters actually changed the result
    if (filtered.length !== this.kikF1Data.length && this.kikF1Data.length > 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'Filters Applied',
        detail: `Showing ${filtered.length} of ${this.kikF1Data.length} records`,
        life: 2000
      });
    }
  }

  // Clear all filters - FIXED
  clearAllFilters(): void {
    this.searchText = '';
    this.selectedYear = null;
    this.selectedMonth = null;
    this.selectedCustomer = null;

    // Ensure kikF1Data is valid before copying
    if (Array.isArray(this.kikF1Data)) {
      this.filteredKikF1Data = [...this.kikF1Data];
    } else {
      this.filteredKikF1Data = [];
    }

    if (this.dt) {
      this.dt.filter('', 'contains', 'global');
    }

    this.messageService.add({
      severity: 'info',
      summary: 'Filters Cleared',
      detail: 'All filters have been reset',
      life: 2000
    });
  }

  // Filter change handlers - FIXED
  onYearChange(): void {
    if (this.selectedYear !== undefined) {
      this.applyFilters();
    }
  }

  onMonthChange(): void {
    this.applyFilters();
  }

  onCustomerChange(): void {
    this.applyFilters();
  }

  // View KIK#F1 data details - FIXED
  viewKikF1Data(item: KikF1Data): void {
    if (item && item.id && this.isValidKikF1Data(item)) {
      this.selectedKikF1Item = { ...item };
      this.showDetailsDialog = true;
    } else {
      console.error('Invalid item provided to viewKikF1Data');
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Data',
        detail: 'Cannot view details for invalid record',
        life: 3000
      });
    }
  }

  // Edit KIK#F1 data - FIXED WITH PROPER INITIALIZATION
  editKikF1Data(item: KikF1Data): void {
    if (item && item.id && this.isValidKikF1Data(item)) {
      // Initialize with all required properties to avoid undefined issues
      this.editingKikF1Item = {
        id: item.id,
        orderId: item.orderId || '',
        customerName: item.customerName || '',
        year: item.year || 0,
        month: item.month || '',
        week: item.week || 0,
        value: item.value || '0',
        quantity: item.quantity || '0'
      };
      this.showEditDialog = true;
    } else {
      console.error('Invalid item provided to editKikF1Data');
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Data',
        detail: 'Cannot edit invalid record',
        life: 3000
      });
    }
  }

  // Save edited KIK#F1 data - FIXED WITH COMPLETE NULL CHECKS
  saveKikF1Data(): void {
  // ✅ FIXED: Allow 0 values in initial validation
  const requiredFields = ['id', 'customerName', 'year', 'month', 'value', 'week'] as const;
  const missingFields: string[] = [];

  requiredFields.forEach(field => {
    const fieldValue = this.editingKikF1Item[field];
    if (!fieldValue ||
        (typeof fieldValue === 'string' && fieldValue.trim() === '') ||
        (typeof fieldValue === 'number' && isNaN(fieldValue) && fieldValue !== 0) ||  // ✅ FIXED: Allow 0
        (field === 'id' && typeof fieldValue !== 'string')) {
      missingFields.push(field);
    }
  });

  if (missingFields.length > 0 || !this.editingKikF1Item.id) {
    this.messageService.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: `Missing required fields: ${missingFields.join(', ')}`,
      life: 4000
    });
    return;
  }

  // ✅ IMPROVED: Ensure year is a valid number
  const yearValue = this.editingKikF1Item.year;
  if (yearValue === undefined || yearValue === null || typeof yearValue !== 'number' || yearValue <= 0) {
    const parsedYear = parseInt(String(yearValue), 10);
    if (isNaN(parsedYear) || parsedYear <= 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Year must be a valid positive number (e.g., 2025)',
        life: 3000
      });
      return;
    }
    this.editingKikF1Item.year = parsedYear;
  }

  // ✅ FIXED: Value validation - ALLOWS 0 ✅
  const valueField = this.editingKikF1Item.value;
  if (valueField === undefined || valueField === null) {
    this.messageService.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Value is required',
      life: 3000
    });
    return;
  }

  let finalValue: string;
  if (typeof valueField === 'number') {
    finalValue = valueField.toFixed(2);  // ✅ 0 becomes "0.00"
  } else if (typeof valueField === 'string') {
    const cleanValue = valueField.replace(/[^\d.-]/g, '');
    const parsedValue = parseFloat(cleanValue);
    // ✅ FIXED: Allow 0 and positive values
    if (isNaN(parsedValue) || !isFinite(parsedValue) || parsedValue < 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Value must be 0 or positive number (e.g., 0, 1234.56)',  // ✅ Clear message
        life: 3000
      });
      return;
    }
    finalValue = parsedValue.toFixed(2);
  } else {
    this.messageService.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Value must be a number or valid number string',
      life: 3000
    });
    return;
  }
  this.editingKikF1Item.value = finalValue;

  // ✅ FIXED: Week dropdown handling (1,2,3,4,5,Total=0)
  const weekValue = this.editingKikF1Item.week;
  let finalWeek: number = 0;

  if (weekValue === undefined || weekValue === null) {
    finalWeek = 0;
  } else if (typeof weekValue === 'number') {
    finalWeek = Math.floor(weekValue);
  } else if (typeof weekValue === 'string') {
    const safeString = weekValue as string;
    const parsedWeek = parseInt(safeString.trim(), 10);
    finalWeek = isNaN(parsedWeek) ? 0 : Math.floor(parsedWeek);
  }

  // ✅ Perfect for your dropdown: 1,2,3,4,5 or 0 (Total)
  this.editingKikF1Item.week = finalWeek;

  // ✅ Clean optional fields
  this.editingKikF1Item.quantity = this.editingKikF1Item.quantity?.toString() || '0';

  // ✅ Safety checks (already validated above)
  if (!this.editingKikF1Item.customerName?.trim() || !this.editingKikF1Item.month?.trim()) {
    this.messageService.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Customer name and month are required',
      life: 3000
    });
    return;
  }

  console.log('✅ Saving KIK#F1 data:', {
    id: this.editingKikF1Item.id,
    customerName: this.editingKikF1Item.customerName,
    year: this.editingKikF1Item.year,
    month: this.editingKikF1Item.month,
    week: finalWeek,           // ✅ 1,2,3,4,5 or 0
    value: finalValue,         // ✅ "0.00", "1234.56"
    quantity: this.editingKikF1Item.quantity
  });

  // ✅ Clean API payload
  const apiData: Partial<KikF1Data> = {
    id: this.editingKikF1Item.id,
    orderId: this.editingKikF1Item.orderId || '',
    customerName: this.editingKikF1Item.customerName!.trim(),
    year: this.editingKikF1Item.year!,
    month: this.editingKikF1Item.month!.trim(),
    week: finalWeek,
    value: finalValue,
    quantity: this.editingKikF1Item.quantity!
  };

  this.workflowDataService.updateKikF1Data(this.editingKikF1Item.id!, apiData).subscribe({
    next: (response) => {
      console.log('✅ Update response:', response);

      const updatedId = this.editingKikF1Item.id!;

      // Update main data array
      if (Array.isArray(this.kikF1Data)) {
        const index = this.kikF1Data.findIndex(item => item.id === updatedId);
        if (index !== -1) {
          this.kikF1Data[index] = { ...this.kikF1Data[index], ...apiData };
        }
      }

      // Update filtered data array
      if (Array.isArray(this.filteredKikF1Data)) {
        const filteredIndex = this.filteredKikF1Data.findIndex(item => item.id === updatedId);
        if (filteredIndex !== -1) {
          this.filteredKikF1Data[filteredIndex] = { ...this.filteredKikF1Data[filteredIndex], ...apiData };
        }
      }

      this.applyFilters();

      const customerName = this.editingKikF1Item.customerName;

      // Close & reset
      this.showEditDialog = false;
      this.editingKikF1Item = {};

      this.messageService.add({
        severity: 'success',
        summary: 'Update Successful',
        detail: `${customerName ? `${customerName} ` : ''}KIK#F1 record updated successfully`,
        life: 3000
      });
    },
    error: (error) => {
      console.error('❌ Error updating KIK#F1 data:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Update Failed',
        detail: 'Failed to update KIK#F1 record. Please try again.',
        life: 5000
      });
    }
  });
}


  // Cancel edit
  cancelEdit(): void {
    this.showEditDialog = false;
    this.editingKikF1Item = {};
  }

  // Delete KIK#F1 data - FIXED TYPE ERRORS
  deleteKikF1Data(item: KikF1Data): void {
    if (!item || !item.id || !item.customerName || !this.isValidKikF1Data(item)) {
      console.error('Invalid item provided to deleteKikF1Data');
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Data',
        detail: 'Cannot delete invalid record',
        life: 3000
      });
      return;
    }

    // Store item details before setting selected item
    const customerName = item.customerName;
    const month = item.month;
    const year = item.year;
    const week = item.week;

    this.selectedKikF1Item = item;
    this.confirmationService.confirm({
      message: `Are you sure you want to delete KIK#F1 data for <strong>${customerName}</strong> (${month} ${year}, Week ${week})?<br><br>This action cannot be undone and will permanently remove this record from the database.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger p-button-outlined',
      acceptIcon: 'pi pi-trash',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.performDelete(customerName, month, year, week);
      },
      reject: () => {
        this.selectedKikF1Item = null;
      }
    });
  }

  private performDelete(customerName: string, month: string, year: number, week: number): void {
    if (!this.selectedKikF1Item?.id || !this.selectedKikF1Item?.orderId) {
      console.error('Cannot delete: missing ID or orderId');
      this.selectedKikF1Item = null;
      this.messageService.add({
        severity: 'error',
        summary: 'Delete Failed',
        detail: 'Cannot delete record due to missing data',
        life: 3000
      });
      return;
    }

    console.log(`Deleting KIK#F1 data for order: ${this.selectedKikF1Item.orderId}`);

    this.workflowDataService.deleteKikF1Data(this.selectedKikF1Item.orderId).subscribe({
      next: (response) => {
        console.log('Delete response:', response);

        // Remove from local arrays
        if (Array.isArray(this.kikF1Data)) {
          this.kikF1Data = this.kikF1Data.filter(item =>
            item && item.id && item.id !== this.selectedKikF1Item?.id
          );
        }

        if (Array.isArray(this.filteredKikF1Data)) {
          this.filteredKikF1Data = this.filteredKikF1Data.filter(item =>
            item && item.id && item.id !== this.selectedKikF1Item?.id
          );
        }

        // Clear the selected item AFTER using its data
        this.selectedKikF1Item = null;

        this.messageService.add({
          severity: 'success',
          summary: 'Delete Successful',
          detail: `KIK#F1 record for ${customerName} (${month} ${year}, Week ${week}) has been deleted successfully`,
          life: 3000
        });
      },
      error: (error) => {
        console.error('Error deleting KIK#F1 data:', error);

        // Clear the selected item even on error
        const errorCustomerName = this.selectedKikF1Item?.customerName || customerName;
        this.selectedKikF1Item = null;

        this.messageService.add({
          severity: 'error',
          summary: 'Delete Failed',
          detail: `Failed to delete KIK#F1 record for ${errorCustomerName}. Please try again.`,
          life: 5000
        });
      }
    });
  }

  // View specific order's KIK#F1 data - FIXED
  viewOrderKikF1Data(orderId: string): void {
    if (!orderId || typeof orderId !== 'string') {
      console.error('Invalid orderId provided to viewOrderKikF1Data');
      return;
    }

    this.loading = true;

    console.log(`Fetching KIK#F1 data for order: ${orderId}`);

    this.workflowDataService.getKikF1DataByOrderId(orderId).subscribe({
      next: (response: any) => {
        console.log('Order KIK#F1 data response:', response);

        let orderData: any[] = [];

        if (response && typeof response === 'object') {
          if (response.kikF1Detail && Array.isArray(response.kikF1Detail)) {
            orderData = response.kikF1Detail;
          } else if (response.data && Array.isArray(response.data)) {
            orderData = response.data;
          } else if (Array.isArray(response)) {
            orderData = response;
          }
        }

        const validData = orderData.filter(this.isValidKikF1Data.bind(this));

        if (validData.length > 0) {
          this.selectedKikF1Item = { ...validData[0] };
          this.showDetailsDialog = true;
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'No Data Found',
            detail: `No KIK#F1 data found for order ${orderId.substring(0, 8)}...`,
            life: 3000
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading order KIK#F1 data:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Load Error',
          detail: `Failed to load KIK#F1 data for order ${orderId.substring(0, 8)}...`,
          life: 5000
        });
        this.loading = false;
      }
    });
  }

  // Utility methods - FIXED WITH COMPLETE NULL CHECKS
  formatCurrency(value: string | number | undefined | null): string {
    if (value === undefined || value === null || value === '0' || value === 0) {
      return '$0.00';
    }

    let numValue: number;

    if (typeof value === 'string') {
      // Remove any existing currency symbols or commas
      const cleanValue = value.replace(/[^\d.-]/g, '');
      numValue = parseFloat(cleanValue);
    } else if (typeof value === 'number') {
      numValue = value;
    } else {
      return '$0.00';
    }

    if (isNaN(numValue) || !isFinite(numValue) || numValue < 0) {
      return '$0.00';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
  }

  formatQuantity(quantity: string | number | undefined | null): string {
    if (quantity === undefined || quantity === null || quantity === '0' || quantity === 0) {
      return '0';
    }

    let numValue: number;

    if (typeof quantity === 'string') {
      const cleanValue = quantity.replace(/[^\d.-]/g, '');
      numValue = parseFloat(cleanValue);
    } else if (typeof quantity === 'number') {
      numValue = quantity;
    } else {
      return '0';
    }

    if (isNaN(numValue) || !isFinite(numValue)) {
      return '0';
    }

    // Return as integer for quantity (no decimals)
    return Math.round(numValue).toString();
  }

  getValueImpact(value: string | number | undefined | null): string {
    if (!value) return 'Low';

    let price: number;
    if (typeof value === 'string') {
      price = parseFloat(value.replace(/[^\d.-]/g, ''));
    } else if (typeof value === 'number') {
      price = value;
    } else {
      return 'Low';
    }

    if (isNaN(price) || price <= 0) return 'Low';

    if (price > 20000) return 'High';
    if (price > 10000) return 'Medium';
    return 'Low';
  }

  getValueImpactClass(value: string | number | undefined | null): string {
    const impact = this.getValueImpact(value);
    switch (impact) {
      case 'High': return 'text-green-600 bg-green-50 font-semibold';
      case 'Medium': return 'text-yellow-600 bg-yellow-50 font-semibold';
      default: return 'text-blue-600 bg-blue-50 font-semibold';
    }
  }

  getMonthDisplay(month: string | undefined | null): string {
    if (!month || typeof month !== 'string') return 'N/A';

    const monthNames: { [key: string]: string } = {
      'January': 'Jan', 'February': 'Feb', 'March': 'Mar', 'April': 'Apr',
      'May': 'May', 'June': 'Jun', 'July': 'Jul', 'August': 'Aug',
      'September': 'Sep', 'October': 'Oct', 'November': 'Nov', 'December': 'Dec'
    };

    return monthNames[month] || month.substring(0, 3);
  }

  // Helper methods for editing form
  private getSafeWeekValue(week: number | string | undefined | null): number {
    if (week === undefined || week === null) {
      return 0;
    }

    if (typeof week === 'number') {
      return week;
    }

    if (typeof week === 'string') {
      const parsed = parseInt(week.trim(), 10);
      return isNaN(parsed) ? 0 : parsed;
    }

    return 0;
  }

  getEditingWeek(): number {
    return this.getSafeWeekValue(this.editingKikF1Item.week);
  }

  setEditingWeek(week: number): void {
    const clampedWeek = Math.max(0, Math.min(52, week));
    this.editingKikF1Item.week = clampedWeek;
  }

  getEditingValue(): string | number {
    const value = this.editingKikF1Item.value;
    if (value === undefined || value === null) {
      return '0';
    }
    if (typeof value === 'string') {
      const parsed = parseFloat(value.replace(/[^\d.-]/g, ''));
      return isNaN(parsed) ? 0 : parsed;
    }
    return value;
  }

  setEditingValue(value: string | number): void {
    if (typeof value === 'number') {
      this.editingKikF1Item.value = value.toFixed(2);
    } else {
      const parsed = parseFloat(value.replace(/[^\d.-]/g, ''));
      this.editingKikF1Item.value = isNaN(parsed) || !isFinite(parsed) ? '0' : parsed.toFixed(2);
    }
  }

  // Export methods
  exportKikF1DataCSV(): void {
    if (!Array.isArray(this.filteredKikF1Data) || this.filteredKikF1Data.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No KIK#F1 data available to export',
        life: 3000
      });
      return;
    }

    // Simple CSV export implementation
    const headers = ['ID', 'Order ID', 'Customer', 'Year', 'Month', 'Week', 'Value', 'Quantity'];
    const csvRows = [
      headers.join(','),
      ...this.filteredKikF1Data.map(item => [
        `"${item.id}"`,
        `"${item.orderId}"`,
        `"${item.customerName}"`,
        item.year,
        `"${item.month}"`,
        item.week,
        item.value,
        item.quantity
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvRows], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `kikf1-data-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    this.messageService.add({
      severity: 'success',
      summary: 'Export Complete',
      detail: `Exported ${this.filteredKikF1Data.length} KIK#F1 records to CSV`,
      life: 3000
    });
  }

  exportKikF1DataPDF(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Export Started',
      detail: 'Generating KIK#F1 data PDF report...',
      life: 3000
    });

    // Placeholder for PDF generation
    setTimeout(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Export Complete',
        detail: `KIK#F1 data PDF report generated (${this.filteredKikF1Data.length || 0} records)`,
        life: 3000
      });
    }, 2000);
  }

  // Dialog close handlers
  closeDetailsDialog(): void {
    this.showDetailsDialog = false;
    this.selectedKikF1Item = null;
  }

  closeEditDialog(): void {
    this.showEditDialog = false;
    this.editingKikF1Item = {};
  }

  // Get detail fields for display dialog
  getDetailFields(): { key: keyof KikF1Data; label: string; type: string }[] {
    return [
      { key: 'customerName', label: 'Customer Name', type: 'text' },
      { key: 'year', label: 'Year', type: 'number' },
      { key: 'month', label: 'Month', type: 'text' },
      { key: 'week', label: 'Week Number', type: 'number' },
      { key: 'value', label: 'Monetary Value', type: 'currency' },
      { key: 'quantity', label: 'Quantity', type: 'text' },
      { key: 'orderId', label: 'Order ID', type: 'text' },
      { key: 'id', label: 'Record ID', type: 'text' }
    ];
  }

  // Get formatted field value for details dialog - FIXED
  getFieldValue(fieldKey: keyof KikF1Data, item: KikF1Data): string {
    if (!item || item[fieldKey] === undefined || item[fieldKey] === null) {
      return '-';
    }

    const value = item[fieldKey];

    switch (fieldKey) {
      case 'value':
        return this.formatCurrency(value);
      case 'year':
      case 'week':
        return typeof value === 'number' ? value.toString() : '-';
      case 'month':
        return this.getMonthDisplay(value as string);
      case 'orderId':
      case 'id':
        if (typeof value === 'string' && value.length > 8) {
          return `${value.substring(0, 8)}...`;
        }
        return typeof value === 'string' ? value : '-';
      case 'customerName':
        return typeof value === 'string' ? value : '-';
      case 'quantity':
        return this.formatQuantity(value);
      default:
        return typeof value === 'string' ? value : (value as any)?.toString() || '-';
    }
  }

  // Calculate totals for summary - FIXED WITH PROPER TYPE HANDLING
  getTotalValue(): number {
    if (!Array.isArray(this.filteredKikF1Data) || this.filteredKikF1Data.length === 0) {
      return 0;
    }

    return this.filteredKikF1Data.reduce((total: number, item: KikF1Data) => {
      if (!this.isValidKikF1Data(item) || !item.value) {
        return total;
      }

      let value: number;
      if (typeof item.value === 'string') {
        value = parseFloat(item.value.replace(/[^\d.-]/g, ''));
      } else {
        value = item.value;
      }

      return total + (isNaN(value) || !isFinite(value) ? 0 : value);
    }, 0);
  }

  getTotalQuantity(): number {
    if (!Array.isArray(this.filteredKikF1Data) || this.filteredKikF1Data.length === 0) {
      return 0;
    }

    return this.filteredKikF1Data.reduce((total: number, item: KikF1Data) => {
      if (!this.isValidKikF1Data(item) || !item.quantity) {
        return total;
      }

      let qty: number;
      if (typeof item.quantity === 'string') {
        qty = parseFloat(item.quantity.replace(/[^\d.-]/g, ''));
      } else {
        qty = item.quantity;
      }

      return total + (isNaN(qty) || !isFinite(qty) ? 0 : qty);
    }, 0);
  }

  // Get unique customers for dropdown filter
  getUniqueCustomers(): string[] {
    if (!Array.isArray(this.kikF1Data) || this.kikF1Data.length === 0) {
      return [];
    }

    return [...new Set(this.kikF1Data
      .filter(this.isValidKikF1Data.bind(this))
      .map(item => item.customerName)
      .filter(name => name && typeof name === 'string' && name.trim() !== '')
    )].sort();
  }
  
}
